import { useMutation } from "@apollo/react-hooks";
import { ApolloError } from "@apollo/client";
import gql from "graphql-tag";
import { useCallback } from "react";
import { ExecutionResult } from "graphql";
import { useSession } from "next-auth/client";
import { useShippingMethodsByLocation } from "./useShippingMethodsByLocation";
import { Mutation, MyCartDraft, InventoryMode } from "src/types/ctgraphql.d";
import getConfig from "next/config";
import { storeConfigs } from "src/utils/storeConfig";
import { StoreKey } from "lib/Intl";

export interface CreateMyCartData {
  createMyCart: Mutation["createMyCart"];
}
interface CreateMyCartVars {
  cart: MyCartDraft;
  storeKey: string;
}

const CREATE_MY_CART = gql`
  mutation createMyCart($cart: MyCartDraft!, $storeKey: KeyReferenceInput) {
    createMyCart(draft: $cart, storeKey: $storeKey) {
      id
      version
      lineItems {
        id
        quantity
      }
    }
  }
`;

interface CreateCartHook {
  data?: CreateMyCartData | null;
  loading: boolean;
  error?: ApolloError;
  createCart: (cart: MyCartDraft) => Promise<ExecutionResult<CreateMyCartData>>;
}

const { publicRuntimeConfig } = getConfig();
const storeConfig =
  storeConfigs[(publicRuntimeConfig.STORE_KEY as StoreKey) || ""];

const useCreateCart = ({
  refetchQueries = [],
  onCompleted,
}: {
  refetchQueries?: string[];
  onCompleted?: (cartData: CreateMyCartData) => void;
} = {}): CreateCartHook => {
  const [session] = useSession();
  const [createCartMutation, { data, loading, error }] = useMutation<
    CreateMyCartData,
    CreateMyCartVars
  >(CREATE_MY_CART, {
    context: { useCT: true },
    refetchQueries,
    onCompleted: (data) => {
      if (onCompleted) {
        onCompleted(data);
      }
    },
  });

  const { data: shippingMethodsByLocationData } = useShippingMethodsByLocation({
    country: storeConfig.shippingAddressCountryCode,
  });

  const createCart = useCallback(
    async (cart: MyCartDraft) => {
      const createdCart = await createCartMutation({
        variables: {
          cart: {
            country: storeConfig.countryCode,
            customerEmail: session?.user?.email,
            shippingAddress: {
              country: storeConfig.shippingAddressCountryCode,
            },
            inventoryMode: InventoryMode.TrackOnly,
            shippingMethod: {
              id: shippingMethodsByLocationData?.shippingMethodsByLocation?.find(
                ({ zoneRates }) =>
                  zoneRates.find(({ shippingRates }) =>
                    shippingRates.find(({ isMatching }) => isMatching)
                  )
              )?.id,
            },
            lineItems: [],
            ...cart,
          },
          storeKey: publicRuntimeConfig.STORE_KEY,
        },
      });

      return createdCart;
    },
    [
      session?.user?.email,

      shippingMethodsByLocationData?.shippingMethodsByLocation,
      createCartMutation,
    ]
  );

  return { createCart, data, loading, error };
};

export { useCreateCart };
